import { Headline3, Spinner } from 'aimo-ui';
import { Center } from 'components/park/styles/styles';
import React, { useCallback } from 'react';
import { useHistoryStore } from 'stores/historyStore';
import styled from 'styled-components/native';
import { HistoryScreenName } from 'utils/historyUtils';
import {
  ChargingSessionHistoryItem,
  NoHistory,
  ParkingSessionHistoryItem,
} from './HistoryComponents';
import { ListFooter, SessionHistoryData } from './HistoryContent';
import SectionListCustom from './SectionListCustom';

const TitleElement = styled(Headline3)`
  margin-bottom: 20px;
  padding-bottom: 8px;
  background-color: ${(p) => p.theme.colors.white};
`;

const Separator = styled.View`
  border-bottom-width: 3px;
  margin-top: -3px;
  border-bottom-color: ${(p) => p.theme.colors.gray50};
`;

const ListItemSeparator = () => <Separator />;

const SessionListFooter: React.FC<{
  isLast?: boolean;
  fetchMore?: () => void;
  loading?: boolean;
}> = ({ isLast, fetchMore, loading }) => {
  return <ListFooter isLast={isLast} fetchMore={fetchMore} loading={loading} />;
};

const FilteredSessionContent: React.FC<{
  sessions: SessionHistoryData;
  sessionsFetchMore?: () => void;
  isLast?: boolean;
  loading?: boolean;
  noData?: boolean;
  historyScreenName?: HistoryScreenName;
}> = ({
  sessions,
  noData,
  sessionsFetchMore,
  isLast,
  loading,
  historyScreenName,
}) => {
  const parkingHistoryFilter = useHistoryStore(
    (state) => state.parkingHistoryViewFilter
  );
  const prepaidPermitHistoryFilter = useHistoryStore(
    (state) => state.prepaidPermitHistoryViewFilter
  );
  const memoizedItem = useCallback(
    ({ section: { groupIndex }, item, index }: any) => {
      if (item.chargingSessionId) {
        return (
          <ChargingSessionHistoryItem
            item={item}
            groupIndex={groupIndex}
            index={index}
          />
        );
      }
      return (
        <ParkingSessionHistoryItem
          item={item}
          groupIndex={groupIndex}
          index={index}
        />
      );
    },
    []
  );

  if (loading) {
    return (
      <Center>
        <Spinner />
      </Center>
    );
  }

  if (noData && !loading) {
    return (
      <NoHistory
        isFailed={parkingHistoryFilter === 'failed'}
        isFree={parkingHistoryFilter === 'free'}
        isBilling={
          parkingHistoryFilter === 'billing' ||
          prepaidPermitHistoryFilter === 'billing'
        }
        isOther={parkingHistoryFilter === 'other'}
        historyScreenName={historyScreenName}
      />
    );
  }

  return (
    <SectionListCustom
      sections={sessions}
      refreshing={loading}
      stickySectionHeadersEnabled={true}
      keyExtractor={({ sessionId = '' }, index) => sessionId + index}
      renderItem={memoizedItem}
      renderSectionHeader={({ section: { groupIndex, title } }) => (
        <TitleElement testID={`${'failed-or-free-session'}-${groupIndex}`}>
          {title}
        </TitleElement>
      )}
      ItemSeparatorComponent={ListItemSeparator}
      ListFooterComponent={
        <SessionListFooter
          isLast={isLast}
          fetchMore={sessionsFetchMore}
          loading={loading}
        />
      }
    />
  );
};

export default FilteredSessionContent;
